import React, { useState, useEffect } from 'react';
import { GraduationCap, Users, Target, Calendar, Trash2, Plus } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Timeline from '../components/Timeline';
import VisionCard from '../components/VisionCard';
import CommitteeCard from '../components/CommitteeCard';
import { useEditMode } from '../hooks/useEditMode';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

interface CommitteeMember {
  id: string;
  name: string;
  role: string;
  image: string;
  order: number;
}

const WhoWeAre = () => {
  const { isEditMode } = useEditMode();
  const [committeeMembers, setCommitteeMembers] = useState<CommitteeMember[]>([
    {
      id: '1',
      name: 'DERYA KÖSE',
      role: 'PRÉSIDENTE',
      image: '',
      order: 0
    },
    {
      id: '2',
      name: 'ALBERTO RODRIGUEZ',
      role: 'TRÉSORIER',
      image: '',
      order: 1
    },
    {
      id: '3',
      name: 'PEDRO JIMÉNEZ',
      role: 'REPRÉSENTANT ASSOCIATIONS',
      image: '',
      order: 2
    },
    {
      id: '4',
      name: 'LIRIDON MEHMETI',
      role: 'MARKETING/ INFORMATIQUE',
      image: '',
      order: 3
    },
    {
      id: '5',
      name: 'GABRIELLE FONCK',
      role: 'EVENTS /PROJETS',
      image: '',
      order: 4
    },
    {
      id: '6',
      name: 'GREGORY CHALLET',
      role: 'MEMBRE HONORAIRE / MARKETING',
      image: '',
      order: 5
    },
  ]);

  const [isAddingMember, setIsAddingMember] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', role: '', image: '', order: 0 });

  useEffect(() => {
    fetchCommitteeMembers();
  }, []);

  const fetchCommitteeMembers = async () => {
    try {
      const docRef = doc(db, 'content', 'committee_members');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.members) {
          const members = data.members.map((member: CommitteeMember, index: number) => ({
            ...member,
            id: member.id || String(index + 1),
            order: member.order ?? index
          }));
          setCommitteeMembers(members.sort((a, b) => a.order - b.order));
        }
      }
    } catch (error) {
      console.error('Error fetching committee members:', error);
    }
  };

  const saveMembers = async (members: CommitteeMember[]) => {
    try {
      await setDoc(doc(db, 'content', 'committee_members'), {
        members: members.map((member, index) => ({ ...member, order: index })),
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error saving members:', error);
      throw error;
    }
  };

  const handleMemberUpdate = async (id: string, field: 'name' | 'role', value: string) => {
    try {
      const updatedMembers = committeeMembers.map(member => {
        if (member.id === id) {
          return { ...member, [field]: value };
        }
        return member;
      });
      
      setCommitteeMembers(updatedMembers);
      await saveMembers(updatedMembers);
    } catch (error) {
      console.error('Error updating member:', error);
      throw error;
    }
  };

  const handleDelete = async (index: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce membre ?')) {
      try {
        const newMembers = [...committeeMembers];
        newMembers.splice(index, 1);
        const reorderedMembers = newMembers.map((member, idx) => ({ ...member, order: idx }));
        setCommitteeMembers(reorderedMembers);
        await saveMembers(reorderedMembers);
      } catch (error) {
        console.error('Error deleting member:', error);
        alert('Erreur lors de la suppression');
      }
    }
  };

  const handleAddMember = async () => {
    if (!newMember.name || !newMember.role) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const newId = String(Math.max(...committeeMembers.map(m => parseInt(m.id))) + 1);
      const updatedMembers = [...committeeMembers, {
        ...newMember,
        id: newId,
        order: committeeMembers.length
      }];
      setCommitteeMembers(updatedMembers);
      await saveMembers(updatedMembers);
      setIsAddingMember(false);
      setNewMember({ name: '', role: '', image: '', order: 0 });
    } catch (error) {
      console.error('Error adding member:', error);
      alert('Error adding new member');
    }
  };

  const onDragEnd = async (result: any) => {
    if (!result.destination) return;

    const items = Array.from(committeeMembers);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const reorderedMembers = items.map((item, index) => ({
      ...item,
      order: index
    }));

    setCommitteeMembers(reorderedMembers);
    try {
      await saveMembers(reorderedMembers);
    } catch (error) {
      console.error('Error saving reordered members:', error);
      alert('Erreur lors de la réorganisation');
    }
  };

  const historyEvents = [
    {
      year: '1966',
      title: 'CRÉATION DE L\'ASDA',
      subtitle: 'Nyon',
      icon: GraduationCap,
    },
    {
      year: '1975',
      title: 'RUDOLF SCHNIDER',
      subtitle: 'Élection du nouveau président',
      icon: Users,
    },
    {
      year: '1988',
      title: 'MICHEL RENDU',
      subtitle: 'Élection du nouveau président',
      icon: Users,
    },
    {
      year: '2020',
      title: 'DERYA KÖSE',
      subtitle: 'Élection de la nouvelle présidente (1ère femme à présider l\'ASDA Genève)',
      icon: Users,
    },
  ];

  const futureEvents = [
    {
      year: '2024',
      title: 'NOUVELLE VISION',
      subtitle: 'Mise en œuvre et application de la nouvelle vision en 2025',
      icon: Target,
    },
    {
      year: '2025',
      title: 'DEVELOPPEMENT DU CHAT BOT',
      subtitle: 'Formations, conseils, recommandations',
      icon: Calendar,
    },
    {
      year: '2026',
      title: 'PROGRAMME DE MENTORING',
      subtitle: 'Mise en place d\'un programme officiel de mentoring',
      icon: Users,
    },
    {
      year: '2027',
      title: 'MISE EN PLACE DE CONFERENCES',
      subtitle: 'Développement des événements professionnels',
      icon: Calendar,
    },
  ];

  const visionItems = [
    {
      number: "1",
      title: "LA FORMATION",
      description: "En collaboration avec des partenaires reconnus, tels que l'AFA, AGFPA & l'IRFA, nous proposons et conseillons des programmes de formation continue adaptés aux besoins du secteur de l'assurance. Notre objectif est d'accompagner nos membres dans le renforcement de leurs compétences et leur évolution professionnelle.",
      icon: GraduationCap
    },
    {
      number: "2",
      title: "L'INNOVATION",
      description: "Nous plaçons l'innovation au cœur de nos actions en intégrant des solutions technologiques modernes, comme notre Chat Bot ASDA. Ce dernier est conçu pour fournir des réponses rapides, des conseils personnalisés et un soutien pratique à nos membres. Ensemble, nous anticipons les défis de demain pour une assurance toujours plus performante et accessible.",
      icon: Target
    },
    {
      number: "3",
      title: "RESEAUTAGE",
      description: "Nous favorisons la création de connexions significatives entre professionnels de l'assurance grâce à des événements, des forums et des ateliers exclusifs. Notre objectif est de bâtir une communauté solide et dynamique où chacun peut élargir son réseau, partager ses expériences et développer des opportunités professionnelles.",
      icon: Users
    },
    {
      number: "4",
      title: "MENTORAT & TRANSMISSION DE CONNAISSANCES",
      description: "Nous valorisons le partage d'expérience intergénérationnelles de professionnels. Notre programme de mentorat soutient le développement des talents tout en enrichissant la communauté grâce à l'expertise de nos membres.",
      icon: Calendar
    }
  ];

  return (
    <div className="bg-beige min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        {/* Histoire Section */}
        <section className="mb-24">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-serif font-bold text-dark mb-4">Notre Histoire</h2>
            <p className="text-lg text-dark/80">QUELQUES DATES ...</p>
          </div>
          <Timeline events={historyEvents} />
        </section>

        {/* Projets Section */}
        <section className="mb-24 bg-cream/30 py-16 rounded-2xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-serif font-bold text-dark mb-4">Nos Projets</h2>
            <p className="text-lg text-dark/80">QUELQUES DATES ...</p>
          </div>
          <Timeline events={futureEvents} />
        </section>

        {/* Vision Section */}
        <section className="mb-24">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-serif font-bold text-dark mb-4">Notre Vision</h2>
            <p className="text-lg text-dark/80">L'AVENIR EN PERSPECTIVE</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {visionItems.map((item, index) => (
              <VisionCard key={index} {...item} />
            ))}
          </div>
        </section>

        {/* Committee Section */}
        <section>
          <div className="text-center mb-12">
            <h2 className="text-4xl font-serif font-bold text-dark mb-4">Le comité de direction</h2>
            <p className="text-lg text-dark/80">Une équipe passionnée à votre service</p>
            {isEditMode && (
              <p className="mt-4 text-sm text-dark/60 italic">
                Glissez-déposez les cartes pour réorganiser les membres du comité
              </p>
            )}
          </div>

          {isEditMode && (
            <div className="mb-8 flex justify-end">
              <button
                onClick={() => setIsAddingMember(true)}
                className="flex items-center gap-2 px-4 py-2 bg-dark text-beige rounded-md hover:bg-dark/90"
              >
                <Plus className="h-5 w-5" />
                Ajouter un membre
              </button>
            </div>
          )}

          {isAddingMember && (
            <div className="mb-8 bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-bold mb-4">Ajouter un nouveau membre</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nom</label>
                  <input
                    type="text"
                    value={newMember.name}
                    onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-dark focus:border-dark"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Rôle</label>
                  <input
                    type="text"
                    value={newMember.role}
                    onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-dark focus:border-dark"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Image URL (optional)</label>
                  <input
                    type="text"
                    value={newMember.image}
                    onChange={(e) => setNewMember({ ...newMember, image: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-dark focus:border-dark"
                  />
                </div>
                <div className="flex justify-end gap-4">
                  <button
                    onClick={() => {
                      setIsAddingMember(false);
                      setNewMember({ name: '', role: '', image: '', order: 0 });
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddMember}
                    className="px-4 py-2 bg-dark text-beige rounded-md hover:bg-dark/90"
                  >
                    Add Member
                  </button>
                </div>
              </div>
            </div>
          )}

          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="committee" direction="horizontal">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                  style={{ display: 'grid' }}
                >
                  {committeeMembers.map((member, index) => (
                    <Draggable
                      key={member.id}
                      draggableId={member.id}
                      index={index}
                      isDragDisabled={!isEditMode}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={{
                            ...provided.draggableProps.style,
                            gridColumn: snapshot.isDragging ? 'span 1' : 'auto'
                          }}
                          className={`relative ${snapshot.isDragging ? 'z-50' : ''}`}
                        >
                          <div 
                            className={`
                              transform transition-transform duration-200
                              ${snapshot.isDragging ? 'scale-105 rotate-2 shadow-2xl' : ''}
                              ${isEditMode ? 'cursor-move' : ''}
                            `}
                          >
                            <CommitteeCard 
                              {...member} 
                              index={index}
                              onUpdate={handleMemberUpdate}
                            />
                            {isEditMode && (
                              <button
                                onClick={() => handleDelete(index)}
                                className="absolute top-2 right-2 p-2 bg-red-600/80 text-white rounded-full hover:bg-red-600 transition-colors"
                                title="Supprimer ce membre"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </section>
      </div>
    </div>
  );
};

export default WhoWeAre;